import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useState } from "react";
import { useController, useFormContext, useFormState } from "react-hook-form";
import { useDialog } from "../../../../utils/hooks/use-dialog";
import { useElementTracking } from "../../../analytics/analytics";
import { CeFontDropdown } from "./ce-font-dropdown";

import type { ElementTracking } from "../../../analytics/analytics";
import type { Fonts } from "./ce-font-dropdown";
import type { FC } from "react";
import type { FieldValues, Path } from "react-hook-form";

const customOption = "Custom Font";

export const CeFontCustomDropdown = <TFieldValues extends FieldValues>({
  name,
  fallbackName,
  label,
  fonts,
  analyticsCategory,
}: {
  name: Path<TFieldValues>;
  fallbackName: Path<TFieldValues>;
  label: string;
  fonts: Fonts;
  onChange?: (value: string) => void;
} & ElementTracking): React.JSX.Element => {
  const { open: dialogOpen, handleOpen: openDialog, handleClose: closeDialog } = useDialog();

  const options = [customOption, ...fonts.webSafe.concat(fonts.web)];

  const { control } = useFormContext<TFieldValues>();
  const { field } = useController({ control, name });
  const { field: fallbackField } = useController({
    control,
    name: fallbackName,
  });
  const { defaultValues } = useFormState({ control });
  const [isEnteringCustomFont, setIsEnteringCustomFont] = useState(false);
  const [prevDefaultValues, setPrevDefaultValues] = useState(defaultValues);
  if (defaultValues !== prevDefaultValues) {
    setPrevDefaultValues(defaultValues);
    setIsEnteringCustomFont(false);
  }

  const hasCustomFont = Boolean(field.value) && !options.includes(field.value);
  const isCustom = isEnteringCustomFont || hasCustomFont;
  const value = isCustom ? customOption : (field.value ?? null);

  const analyticsProps = useElementTracking(analyticsCategory, name, "check");

  const groupBy = useCallback(
    (font: string): string => {
      if (font === customOption) return "";
      return fonts.webSafe.includes(font) ? "Web-Safe Fonts" : "Web Fonts";
    },
    [fonts],
  );

  const onChange = useCallback(
    (val: string | null) => {
      fallbackField.onChange(null);

      if (val === customOption) {
        setIsEnteringCustomFont(true);
        field.onChange(null);
        openDialog();
        return;
      }

      setIsEnteringCustomFont(false);
      field.onChange(val);
    },
    [fallbackField, field, openDialog],
  );

  return (
    <>
      <Autocomplete
        value={value}
        onChange={(_, val) => onChange(val)}
        options={options}
        groupBy={fonts.web.length > 0 ? groupBy : undefined}
        renderOption={(params, option) => <Option key={option} params={params} option={option} />}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={label}
            fullWidth
            sx={{
              "& .MuiInputBase-input": {
                fontFamily: value && !isCustom ? value : "inherit",
                color: isCustom ? "primary.main" : "inherit",
              },
            }}
            {...analyticsProps}
          />
        )}
      />
      {isCustom && (
        <>
          <TextField
            {...field}
            value={field.value ?? ""}
            label="Custom Font"
            placeholder="Custom Font"
            fullWidth
            onChange={(event) => {
              setIsEnteringCustomFont(!event.target.value);
              field.onChange(event);
            }}
            sx={{
              "& .MuiInputBase-input": {
                fontFamily: field.value || "inherit",
              },
            }}
            {...analyticsProps}
          />
          <CeFontDropdown<TFieldValues>
            analyticsCategory={analyticsCategory}
            name={fallbackName}
            label="Fallback Font Family"
            fonts={fonts}
          />
        </>
      )}
      <WarningModal open={dialogOpen} closeModal={closeDialog} />
    </>
  );
};

const Option: FC<{
  params: React.HTMLAttributes<HTMLLIElement> & { key: React.Key };
  option: string;
}> = ({ params, option }) => {
  if (option === customOption) {
    return (
      <Typography {...params} key={option} color="primary">
        {option}
      </Typography>
    );
  }

  return (
    <Typography {...params} sx={{ fontFamily: option }} key={option}>
      {option}
    </Typography>
  );
};

const WarningModal: FC<{ open: boolean; closeModal: () => void }> = ({ open, closeModal }) => (
  <Dialog open={open} onClose={closeModal}>
    <DialogTitle>Custom fonts</DialogTitle>
    <DialogContent>
      <DialogContentText component="div">
        <p>
          Enter the name of your custom font into the Custom font name field. The name must be
          entered exactly as it is named on your computer, otherwise it will not display correctly.
        </p>
        <p>
          To view custom fonts, all ChangeEngine editors, and your recipients, must have the font
          installed on their machines already. You must choose a Fallback font to display if they do
          not have the custom font installed.
        </p>
      </DialogContentText>
    </DialogContent>
    <DialogActions>
      <Button
        data-analytics-id="email-design-custom-font-dialog-continue"
        variant="outlined"
        onClick={closeModal}
      >
        Continue
      </Button>
    </DialogActions>
  </Dialog>
);
